import React, { ReactElement, useMemo, JSX } from 'react';
import { toJS } from 'mobx';
import { observer } from 'mobx-react';
import {
  ApiBestPricePackageModel,
  ApiItemType,
  ApiPackageModel,
  ApiPagination,
  ApiSortOption
} from '@ibe/api';
import { itemListEvent } from '@ibe/services';
import PackageStore from './PackageStore';
import PackageListItem, { PackageItemSelection } from '../../Items/Package/List/PackageListItem';
import OverlayContainer from '../../Components/UI/LoadingOverlay/OverlayContainer';
import { ServiceList } from '../../SubModules/ServiceList/ServiceList';
import Overlay from '../../Components/UI/LoadingOverlay/Overlay';
import Props from './externals';
import { CustomListOptions } from '../../SubModules/ServiceList/externals';
import PackageParams from '../../SubModules/PackageDetails/PackageParams';

const List = observer(
  (props: {
    store: PackageStore;
    subType?: ApiItemType;
    onSelect?: (selection: PackageItemSelection) => void;
    onDetail: Props['onDetail'];
    showMultipleDurations: boolean;
    sorting?: ApiSortOption[];
    sortingType: Props['sortingType'];
    travelDateSelect?: boolean;
    doBestPriceSearch?: boolean;
    showSearchParamsPerItem?: boolean;
    renderListItem?: (
      item: ApiPackageModel,
      index?: number,
      onSelect?: (selection: Partial<PackageItemSelection>) => void,
      eachItemSearchParams?: PackageParams
    ) => ReactElement;
    paginationProps?: Props['paginationProps'];
    customListOptions?: CustomListOptions;
    singleSelectCategories?: boolean;
  }) => {
    const {
      store,
      subType,
      onDetail,
      showMultipleDurations,
      sorting,
      sortingType,
      renderListItem,
      doBestPriceSearch,
      paginationProps,
      customListOptions,
      showSearchParamsPerItem,
      singleSelectCategories
    } = props;
    const onPackageSelect = (selection: Partial<PackageItemSelection>): void => {
      const params = toJS(store.searchParams);
      params.packageDetailId = selection.detail?.id;
      params.travelDateId = selection.travelDate?.id;
      params.itemId = selection.item?.id;
      store.setSearchParams(params);
      if (onDetail) {
        onDetail(store.searchParams, store.listOptions);
      }
    };

    const createSortingOptions = (): ApiSortOption[] => {
      return [ApiSortOption.NAME, ApiSortOption.PRICE, ApiSortOption.PROMOTIONS];
    };

    const serviceListStoreCallback = async (a?: ApiPagination): Promise<void> => {
      store.setIsLoading(true);
      store.resetDetail();
      if (subType) {
        store.setSearchParams({
          ...store.searchParams,
          preSelectedCharacteristics: [],
          inventories: []
        });
        await store.loadPackages(subType, doBestPriceSearch);
        if (store.list?.length > 0) {
          itemListEvent.broadcast({ items: store.list });
        }
      }
      store.setIsLoading(false);
      if (paginationProps?.onPageChangeListener) paginationProps.onPageChangeListener(a);
    };

    return (
      <OverlayContainer>
        <ServiceList
          items={store.list}
          fullItemCount={store.listCountFull}
          filteredItemCount={store.listCountFiltered}
          filterProps={store.getFilterProperties(serviceListStoreCallback, singleSelectCategories)}
          paginationProps={store.getPaginationProperties(serviceListStoreCallback)}
          sortProps={store.getSortProps(
            serviceListStoreCallback,
            sorting ? sorting : createSortingOptions(),
            sortingType
          )}
          renderItem={(item): JSX.Element => (
            <RenderListItemWrapper
              key={item.id}
              item={item}
              store={store}
              showSearchParamsPerItem={showSearchParamsPerItem}
              onPackageSelect={onPackageSelect}
              renderListItem={renderListItem}
              showMultipleDurations={showMultipleDurations}
            />
          )}
          customListOptions={customListOptions}
        />
        {store.isLoading && <Overlay />}
      </OverlayContainer>
    );
  }
);

const RenderListItemWrapper = ({
  store,
  showSearchParamsPerItem,
  showMultipleDurations,
  onPackageSelect,
  renderListItem,
  ...props
}: {
  store: PackageStore;
  item: ApiPackageModel | ApiBestPricePackageModel;
  showSearchParamsPerItem?: boolean;
  showMultipleDurations?: boolean;
  onPackageSelect: (selection: Partial<PackageItemSelection>) => void;
  renderListItem?: (
    item: ApiPackageModel,
    index?: number,
    onSelect?: (selection: Partial<PackageItemSelection>) => void,
    eachItemSearchParams?: PackageParams
  ) => ReactElement;
}) => {
  const item = props.item as ApiPackageModel;

  const itemSearchParams = useMemo(() => {
    if (!showSearchParamsPerItem) {
      return undefined;
    }
    const travelDate = item?.travelDates?.[0];
    if (showMultipleDurations) {
      return undefined;
    }
    const selectedDetail = item.packageDetails?.find(d => d.travelDateId === travelDate?.id);

    const params = toJS(store.searchParams);
    params.packageDetailId = selectedDetail?.id;
    params.travelDateId = travelDate?.id;
    params.itemId = item?.id;
    params.listOptions = store.listOptions;

    return params;
  }, [showMultipleDurations, item, store.searchParams]);

  return (
    <>
      {renderListItem ? (
        renderListItem(
          item,
          store.list ? store.list.indexOf(item) : undefined,
          (s): void => onPackageSelect(s),
          itemSearchParams
        )
      ) : (
        <PackageListItem
          showMultipleDurations={showMultipleDurations}
          item={item}
          onSelect={(s): void => onPackageSelect(s)}
          eachItemSearchParams={itemSearchParams}
        />
      )}
    </>
  );
};

export default List;
